import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalBody, ModalFooter, useToast } from '../sectepe-ui/index';
import salesCampaignService, { SalesCampaign, ContactLead } from '../services/salesCampaignService';
import { PageHeader, EmptyState, LoadingState, StatusBadge, useConfirm } from '../components/ui';
import { Icon } from '../components/icons';
import { SalesProcessBar } from '../vertrieb/SalesProcessBar';
import { KpiRow } from '../vertrieb/KpiRow';
import { fehlerText } from '../vertrieb/format';
import { kampagnenStatus } from './SalesCampaignsPage';
import IcebreakerQuality from '../components/IcebreakerQuality';
import { RechteHinweis } from '../vertrieb/RechteHinweis';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';

/** Prisma liefert JSON-Felder je nach Route mal als String, mal geparst. */
const jsonWert = <T,>(wert: unknown, standard: T): T => {
  if (wert === null || wert === undefined || wert === '') return standard;
  if (typeof wert !== 'string') return wert as T;
  try {
    return JSON.parse(wert) as T;
  } catch {
    return standard;
  }
};

const ort = (c: ContactLead) => [c.postalCode, c.city].filter(Boolean).join(' ') || c.region || c.location || '';

const SalesCampaignDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { push } = useToast();
  const { confirm } = useConfirm();
  // Welle 2: Qualifizieren, Icebreaker, Firma anlegen und Kontakt loeschen
  // verlangen vertrieb.bearbeiten; der CSV-Export ist ein GET.
  const { darf } = useBerechtigung();
  const darfBearbeiten = darf('vertrieb.bearbeiten');
  const [kampagne, setKampagne] = useState<SalesCampaign | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [nurQualifizierte, setNurQualifizierte] = useState(false);
  const [offen, setOffen] = useState<ContactLead | null>(null);
  const [angelegt, setAngelegt] = useState<Record<string, string>>({});
  const [generiertIb, setGeneriertIb] = useState(false);
  const [variantenAnzahl, setVariantenAnzahl] = useState(2);

  const laden = useCallback(async () => {
    if (!id) return;
    try {
      setKampagne((await salesCampaignService.getCampaign(id)) ?? null);
      setFehler(null);
    } catch (e) {
      setFehler(fehlerText(e, 'Kampagne konnte nicht geladen werden'));
    } finally {
      setLaedt(false);
    }
  }, [id]);

  useEffect(() => { laden(); }, [laden]);

  const qualifizieren = async (c: ContactLead, wert: boolean) => {
    try {
      await salesCampaignService.updateContactLead(c.id, { isQualified: wert });
      await laden();
      if (offen?.id === c.id) setOffen({ ...offen, isQualified: wert });
    } catch (e) {
      setFehler(fehlerText(e, 'Kontakt konnte nicht geändert werden'));
    }
  };

  const loeschen = async (c: ContactLead) => {
    const ok = await confirm({ title: 'Kontakt löschen?', message: c.companyName, confirmLabel: 'Löschen', tone: 'danger' });
    if (!ok) return;
    try {
      await salesCampaignService.deleteContactLead(c.id);
      if (offen?.id === c.id) setOffen(null);
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Löschen fehlgeschlagen'));
    }
  };

  // Gespeicherte Zuordnung (companyId) oder gerade eben angelegt.
  const firmaVon = (c: ContactLead) => angelegt[c.id] || c.companyId || null;

  const alsFirma = async (c: ContactLead) => {
    try {
      // Backend uebernimmt alle Felder und merkt sich die Firma am Kontakt.
      const { company } = await salesCampaignService.alsFirma(c.id);
      setAngelegt((alt) => ({ ...alt, [c.id]: company.id }));
      push({ message: `Firma „${company.name}“ in der Akte angelegt.`, severity: 'success' });
      navigate(`/companies/${company.id}`);
    } catch (e) {
      setFehler(fehlerText(e, 'Firma konnte nicht angelegt werden'));
    }
  };

  /** Icebreaker fuer die Kontakte erzeugen (qualifizierte zuerst, sonst alle). */
  const icebreakerErzeugen = async () => {
    if (!kampagne) return;
    const alle = kampagne.contacts ?? [];
    const qualifizierte = alle.filter((c) => c.isQualified);
    const ziele = qualifizierte.length > 0 ? qualifizierte : alle;
    const umfang = qualifizierte.length > 0 ? 'qualifizierte' : 'alle';
    const ok = await confirm({
      title: 'Icebreaker generieren?',
      message: `Icebreaker für ${ziele.length} ${umfang} Kontakt(e) generieren? Das kann je nach Anzahl etwas dauern.`,
      confirmLabel: 'Generieren',
    });
    if (!ok) return;
    setGeneriertIb(true);
    try {
      const ergebnis = await salesCampaignService.generateContactIcebreakers(kampagne.id, {
        contactIds: qualifizierte.length > 0 ? qualifizierte.map((c) => c.id) : undefined,
        channels: ['email', 'linkedin'],
        autoSources: ['website'],
        language: 'de',
        variantCount: variantenAnzahl,
      });
      push({
        message: `Icebreaker generiert: ${ergebnis.generated}${ergebnis.failed > 0 ? `, fehlgeschlagen: ${ergebnis.failed}` : ''}`,
        severity: ergebnis.failed > 0 ? 'warning' : 'success',
      });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Fehler bei der Icebreaker-Generierung'));
    } finally {
      setGeneriertIb(false);
    }
  };

  const exportieren = async () => {
    if (!kampagne) return;
    try {
      const csv = await salesCampaignService.exportContacts(kampagne.id, 'csv');
      salesCampaignService.downloadCSV(csv, `kampagne-${kampagne.name}.csv`);
    } catch (e) {
      setFehler(fehlerText(e, 'Export fehlgeschlagen'));
    }
  };

  if (laedt) {
    return (
      <div className="page-container">
        <SalesProcessBar stepId="zielfirmen" />
        <LoadingState rows={4} label="Kampagne wird geladen" />
      </div>
    );
  }

  if (!kampagne) {
    return (
      <div className="page-container">
        <SalesProcessBar stepId="zielfirmen" />
        <EmptyState
          icon="megaphone"
          title="Kampagne nicht gefunden"
          description={fehler ?? 'Vielleicht wurde sie gelöscht.'}
          action={{ label: 'Zu den Kampagnen', to: '/sales-campaigns' }}
        />
      </div>
    );
  }

  const st = kampagnenStatus(kampagne.status);
  const kontakte = kampagne.contacts ?? [];
  const qualifiziert = kontakte.filter((c) => c.isQualified);
  const sichtbar = nurQualifizierte ? qualifiziert : kontakte;
  const branchen = jsonWert<string[]>(kampagne.targetIndustries, []);
  const kriterien = jsonWert<SalesCampaign['targetCriteria']>(kampagne.targetCriteria, undefined);

  return (
    <div className="page-container-wide">
      <SalesProcessBar stepId="zielfirmen" label={kampagne.name} />
      <PageHeader
        eyebrow={<Link to="/sales-campaigns"><Icon name="arrow-left" size={12} /> Alle Kampagnen</Link>}
        title={kampagne.name}
        subtitle={kampagne.description || undefined}
        actions={(
          <>
            <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
            {darfBearbeiten && kontakte.length > 0 && (
              <label className="text-xs">
                Varianten{' '}
                <select
                  value={variantenAnzahl}
                  onChange={(e) => setVariantenAnzahl(Number(e.target.value))}
                  disabled={generiertIb}
                  aria-label="Icebreaker-Varianten je Kontakt"
                >
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                </select>
              </label>
            )}
            {darfBearbeiten && kontakte.length > 0 && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={icebreakerErzeugen}
                disabled={generiertIb}
                title="Personalisierte Icebreaker für die Kontakte generieren (qualifizierte zuerst)"
              >
                <Icon name="sparkles" size={14} /> {generiertIb ? 'Generiere…' : 'Icebreaker generieren'}
              </button>
            )}
            {kontakte.length > 0 && (
              <button type="button" className="btn btn-secondary" onClick={exportieren}>
                <Icon name="file-text" size={14} /> CSV
              </button>
            )}
            {darfBearbeiten && qualifiziert.length > 0 && (
              <Link to="/lead-sync" className="btn btn-primary">
                Weiter: Leads &amp; Outreach <Icon name="arrow-right" size={14} />
              </Link>
            )}
          </>
        )}
      />

      <RechteHinweis was="Kontakte qualifizieren, Icebreaker erzeugen und als Firma anlegen" />

      {fehler && <div className="alert alert-error">{fehler}</div>}

      <KpiRow
        aria-label="Kampagnen-Kennzahlen"
        items={[
          { label: 'Kontakte gefunden', value: kontakte.length },
          { label: 'qualifiziert', value: qualifiziert.length, tone: qualifiziert.length ? 'good' : 'neutral' },
          { label: 'offen', value: kontakte.length - qualifiziert.length },
        ]}
      />

      <section className="card akte-block" aria-label="Suchkriterien">
        <div className="akte-block-kopf"><h2>Suchkriterien</h2></div>
        <dl className="sales-facts">
          <div><dt>Region</dt><dd>{kampagne.targetRegion}</dd></div>
          {branchen.length > 0 && <div><dt>Branchen</dt><dd>{branchen.join(', ')}</dd></div>}
          {kriterien?.nearLocations && <div><dt>Nähe zu</dt><dd>{kriterien.nearLocations}</dd></div>}
          {(kriterien?.specificLocations?.length ?? 0) > 0 && <div><dt>Standorte</dt><dd>{kriterien!.specificLocations!.join(', ')}</dd></div>}
          {kriterien?.additionalCriteria && <div><dt>Weitere</dt><dd>{kriterien.additionalCriteria}</dd></div>}
        </dl>
      </section>

      <div className="toolbar">
        <h2 className="section-title toolbar__title">Kontakte ({sichtbar.length})</h2>
        <div className="toolbar__group toolbar__group--right">
          <label className="text-sm">
            <input type="checkbox" checked={nurQualifizierte} onChange={(e) => setNurQualifizierte(e.target.checked)} />{' '}
            nur qualifizierte
          </label>
        </div>
      </div>

      {kontakte.length === 0 ? (
        <EmptyState
          icon="building"
          title={kampagne.status === 'draft' ? 'Noch keine Recherche gestartet' : kampagne.status === 'researching' ? 'Recherche läuft' : 'Keine Kontakte gefunden'}
          description={kampagne.status === 'draft'
            ? 'Starte die KI-Recherche in der Kampagnenliste – sie füllt diese Tabelle.'
            : kampagne.status === 'researching'
              ? 'Die Ergebnisse erscheinen hier, sobald die Recherche fertig ist.'
              : 'Kriterien lockern und die Recherche erneut starten.'}
          action={{ label: 'Zur Kampagnenliste', to: '/sales-campaigns' }}
        />
      ) : sichtbar.length === 0 ? (
        <EmptyState compact icon="check" title="Noch kein Kontakt qualifiziert" description={darfBearbeiten ? 'Markiere in der Liste, welche Firmen passen.' : 'Bisher ist keine Firma als passend markiert.'} action={{ label: 'Alle zeigen', onClick: () => setNurQualifizierte(false) }} />
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Unternehmen</th><th>Branche</th><th>Ort</th><th>Ansprechpartner</th><th>Status</th>{darfBearbeiten && <th />}
              </tr>
            </thead>
            <tbody>
              {sichtbar.map((c) => (
                <tr key={c.id} className={`tabelle-zeile ${c.isQualified ? 'zeile-qualifiziert' : ''}`} onClick={() => setOffen(c)}>
                  <td>
                    <strong>{c.companyName}</strong>
                    {c.icebreakerGeneratedAt && (
                      <>
                        {' '}
                        <StatusBadge tone="success" icon="sparkles">Icebreaker</StatusBadge>
                      </>
                    )}
                    {c.website && (
                      <div className="text-xs">
                        <a href={c.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                          {c.website.replace(/^https?:\/\/(www\.)?/, '').slice(0, 40)}
                        </a>
                      </div>
                    )}
                  </td>
                  <td>{c.industry || '—'}</td>
                  <td>{ort(c) || '—'}</td>
                  <td>
                    {c.contactPerson || '—'}
                    {c.jobTitle && <div className="text-xs text-secondary">{c.jobTitle}</div>}
                  </td>
                  <td>
                    {firmaVon(c)
                      ? <Link to={`/companies/${firmaVon(c)}`} onClick={(e) => e.stopPropagation()}><StatusBadge tone="success" icon="building">in der Akte</StatusBadge></Link>
                      : c.isQualified
                        ? <StatusBadge tone="success" icon="check">qualifiziert</StatusBadge>
                        : <StatusBadge tone="neutral">offen</StatusBadge>}
                  </td>
                  {darfBearbeiten && (
                    <td className="r" onClick={(e) => e.stopPropagation()}>
                      <div className="btn-group">
                        {c.isQualified
                          ? <button type="button" className="btn btn-sm btn-ghost" onClick={() => qualifizieren(c, false)}>zurücksetzen</button>
                          : <button type="button" className="btn btn-sm btn-secondary" onClick={() => qualifizieren(c, true)}>qualifizieren</button>}
                        {!firmaVon(c) && (
                          <button type="button" className="btn btn-sm btn-primary" onClick={() => alsFirma(c)}>
                            <Icon name="building" size={12} /> als Firma anlegen
                          </button>
                        )}
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => loeschen(c)} aria-label={`${c.companyName} löschen`}>
                          <Icon name="trash" size={14} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={Boolean(offen)} onOpenChange={(o) => { if (!o) setOffen(null); }}>
        {offen && (
          <ModalContent className="detail-modal">
            <ModalHeader>
              <ModalTitle>
                {offen.companyName}{' '}
                {offen.isQualified ? <StatusBadge tone="success" icon="check">qualifiziert</StatusBadge> : <StatusBadge tone="neutral">offen</StatusBadge>}
              </ModalTitle>
              <ModalDescription>{[offen.industry, ort(offen)].filter(Boolean).join(' · ') || 'Kontakt aus der Recherche'}</ModalDescription>
            </ModalHeader>
            <ModalBody>
              {offen.description && <p>{offen.description}</p>}
              <dl className="sales-facts">
                {offen.address && <div><dt>Adresse</dt><dd>{offen.address}{ort(offen) && <>, {ort(offen)}</>}</dd></div>}
                {offen.location && <div><dt>Standortbezug</dt><dd>{offen.location}</dd></div>}
                {offen.contactPerson && <div><dt>Ansprechpartner</dt><dd>{offen.contactPerson}{offen.jobTitle && ` – ${offen.jobTitle}`}</dd></div>}
                {offen.phone && <div><dt>Telefon</dt><dd><a href={`tel:${offen.phone}`}>{offen.phone}</a></dd></div>}
                {offen.email && <div><dt>E-Mail</dt><dd><a href={`mailto:${offen.email}`}>{offen.email}</a></dd></div>}
                {offen.website && <div><dt>Website</dt><dd><a href={offen.website} target="_blank" rel="noopener noreferrer">{offen.website}</a></dd></div>}
                {offen.linkedInUrl && <div><dt>LinkedIn</dt><dd><a href={offen.linkedInUrl} target="_blank" rel="noopener noreferrer">Profil ansehen</a></dd></div>}
                {offen.notes && <div><dt>Notizen</dt><dd>{offen.notes}</dd></div>}
                {offen.qualificationNotes && <div><dt>Qualifizierung</dt><dd>{offen.qualificationNotes}</dd></div>}
              </dl>
              <IcebreakerEditor key={offen.id} contact={offen} onSaved={laden} darfBearbeiten={darfBearbeiten} />
            </ModalBody>
            <ModalFooter>
              {darfBearbeiten && (offen.isQualified
                ? <button type="button" className="btn btn-ghost" onClick={() => qualifizieren(offen, false)}>Qualifizierung zurücksetzen</button>
                : <button type="button" className="btn btn-secondary" onClick={() => qualifizieren(offen, true)}>Qualifizieren</button>)}
              {firmaVon(offen)
                ? <Link to={`/companies/${firmaVon(offen)}`} className="btn btn-primary">Zur Firmenakte</Link>
                : darfBearbeiten && <button type="button" className="btn btn-primary" onClick={() => alsFirma(offen)}><Icon name="building" size={14} /> Als Firma anlegen</button>}
            </ModalFooter>
          </ModalContent>
        )}
      </Modal>
    </div>
  );
};

// ============================================================
// Icebreaker je Kontakt: Review, A/B-Auswahl, Bearbeiten, Speichern
// ============================================================

interface IcebreakerVariant {
  label: string;
  angle?: string;
  subject?: string;
  icebreaker: string;
}
interface IcebreakerVariants {
  email: IcebreakerVariant[];
  linkedin: IcebreakerVariant[];
}

const parseIcebreakerVariants = (raw?: string): IcebreakerVariants => {
  if (!raw) return { email: [], linkedin: [] };
  try {
    const v = JSON.parse(raw);
    return {
      email: Array.isArray(v?.email) ? v.email : [],
      linkedin: Array.isArray(v?.linkedin) ? v.linkedin : [],
    };
  } catch {
    return { email: [], linkedin: [] };
  }
};

const IcebreakerEditor: React.FC<{
  contact: ContactLead;
  onSaved: () => void | Promise<void>;
  /** Ohne vertrieb.bearbeiten: Texte nur lesen, keine Knoepfe (Welle 2). */
  darfBearbeiten: boolean;
}> = ({ contact, onSaved, darfBearbeiten }) => {
  const { push } = useToast();
  const [ibEmail, setIbEmail] = useState(contact.icebreakerEmail || '');
  const [ibSubject, setIbSubject] = useState(contact.icebreakerSubject || '');
  const [ibLinkedin, setIbLinkedin] = useState(contact.icebreakerLinkedin || '');
  const [variants, setVariants] = useState<IcebreakerVariants>(() => parseIcebreakerVariants(contact.icebreakerVariants));
  const [generated, setGenerated] = useState(!!contact.icebreakerGeneratedAt);
  const [variantCount, setVariantCount] = useState(2);
  const [regenerating, setRegenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const firstName = (contact.contactPerson || '').trim().split(/\s+/)[0];

  const handleRegenerate = async () => {
    setRegenerating(true);
    setError('');
    try {
      await salesCampaignService.generateContactIcebreakers(contact.campaignId, {
        contactIds: [contact.id],
        channels: ['email', 'linkedin'],
        autoSources: ['website'],
        language: 'de',
        variantCount,
      });
      const fresh = await salesCampaignService.getContactLead(contact.id);
      setVariants(parseIcebreakerVariants(fresh.icebreakerVariants));
      setIbEmail(fresh.icebreakerEmail || '');
      setIbSubject(fresh.icebreakerSubject || '');
      setIbLinkedin(fresh.icebreakerLinkedin || '');
      setGenerated(true);
      await onSaved();
    } catch (e) {
      setError(fehlerText(e, 'Fehler bei der Generierung'));
    } finally {
      setRegenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await salesCampaignService.updateContactLead(contact.id, {
        icebreakerEmail: ibEmail,
        icebreakerSubject: ibSubject,
        icebreakerLinkedin: ibLinkedin,
      });
      push({ message: 'Icebreaker gespeichert.', severity: 'success' });
      await onSaved();
    } catch (e) {
      setError(fehlerText(e, 'Fehler beim Speichern'));
    } finally {
      setSaving(false);
    }
  };

  const renderVariantChooser = (list: IcebreakerVariant[], activeText: string, onPick: (v: IcebreakerVariant) => void) => {
    if (list.length < 2 || !darfBearbeiten) return null;
    return (
      <div className="btn-group" style={{ flexWrap: 'wrap', marginBottom: '0.4rem' }}>
        {list.map((v, i) => (
          <button
            key={i}
            type="button"
            className={`btn btn-sm ${activeText === v.icebreaker ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => onPick(v)}
            title={v.angle || ''}
          >
            {v.label || String.fromCharCode(65 + i)}
          </button>
        ))}
      </div>
    );
  };

  return (
    <section className="card akte-block" aria-label="Icebreaker">
      <div className="akte-block-kopf">
        <h3><Icon name="sparkles" size={14} /> Icebreaker</h3>
        {darfBearbeiten && (
          <div className="btn-group" style={{ alignItems: 'center' }}>
            <label className="text-xs">
              Varianten{' '}
              <select value={variantCount} onChange={(e) => setVariantCount(Number(e.target.value))} disabled={regenerating}>
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
              </select>
            </label>
            <button type="button" className="btn btn-secondary btn-sm" onClick={handleRegenerate} disabled={regenerating}>
              {regenerating ? 'Generiere…' : generated ? 'Neu generieren' : 'Generieren'}
            </button>
          </div>
        )}
      </div>
      {darfBearbeiten && (
        <p className="text-xs text-secondary">
          Bei mehreren Varianten A/B/C wählen, dann bei Bedarf anpassen und speichern.
        </p>
      )}

      <div className="form-group">
        <label className="form-label">Betreff (E-Mail)</label>
        <input className="form-input" value={ibSubject} disabled={!darfBearbeiten} onChange={(e) => setIbSubject(e.target.value)} />
      </div>
      <div className="form-group">
        <label className="form-label">Icebreaker E-Mail</label>
        {renderVariantChooser(variants.email, ibEmail, (v) => {
          setIbEmail(v.icebreaker);
          setIbSubject(v.subject || '');
        })}
        <textarea className="form-textarea" rows={3} value={ibEmail} disabled={!darfBearbeiten} onChange={(e) => setIbEmail(e.target.value)} />
        {darfBearbeiten && <IcebreakerQuality text={ibEmail} kind="email" contact={{ firstName, company: contact.companyName }} />}
      </div>
      <div className="form-group">
        <label className="form-label">Icebreaker LinkedIn</label>
        {renderVariantChooser(variants.linkedin, ibLinkedin, (v) => setIbLinkedin(v.icebreaker))}
        <textarea className="form-textarea" rows={3} value={ibLinkedin} disabled={!darfBearbeiten} onChange={(e) => setIbLinkedin(e.target.value)} />
        {darfBearbeiten && <IcebreakerQuality text={ibLinkedin} kind="linkedin" contact={{ firstName, company: contact.companyName }} />}
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {darfBearbeiten && (
        <div className="btn-group">
          <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Speichere…' : 'Icebreaker speichern'}
          </button>
        </div>
      )}
    </section>
  );
};

export default SalesCampaignDetailPage;
